import { Loader2, Trash2 } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useUpdateCalendarEvent, useDeleteCalendarEvent } from "@/hooks/use-calendar-events";
import { useUIStore } from "@/stores/ui-store";
import { formatDateTime } from "@/lib/format-date";
import type { CalendarEvent } from "@/api/types";
import { parseTimestamp } from "./calendar-utils";

export function EventInspector({ data }: { data: unknown }) {
  const event = data as CalendarEvent;
  const updateEvent = useUpdateCalendarEvent();
  const deleteEvent = useDeleteCalendarEvent();
  const { closeInspector } = useUIStore();

  const startDate = parseTimestamp(event.startDate);
  const endDate = parseTimestamp(event.endDate);

  const handleComplete = () => {
    updateEvent.mutate({ eventId: event.id, status: "completed" });
  };

  const handleCancel = () => {
    updateEvent.mutate({ eventId: event.id, status: "cancelled" });
  };

  const handleDelete = () => {
    if (confirm("Delete this event?")) {
      deleteEvent.mutate({ eventId: event.id }, {
        onSuccess: () => closeInspector(),
      });
    }
  };

  const canComplete = event.status === "scheduled" || event.status === "in_progress";
  const canCancel = event.status !== "completed" && event.status !== "cancelled";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{event.title}</h3>
        <div className="mt-2 flex items-center gap-2">
          <StatusBadge status={event.status} />
          {event.source !== "manual" && (
            <span className="text-xs text-muted-foreground">
              {event.source === "edc_trigger" ? "EDC Triggered" : "Forecast"}
            </span>
          )}
        </div>
      </div>

      {event.description && (
        <p className="text-sm text-muted-foreground">{event.description}</p>
      )}

      <div className="space-y-2">
        <Detail label="Start" value={formatDateTime(startDate)} />
        <Detail label="End" value={formatDateTime(endDate)} />

        {event.isAllDay && <Detail label="All Day" value="Yes" />}
        {event.location && <Detail label="Location" value={event.location} />}
        {event.category && <Detail label="Category" value={event.category.replace(/_/g, " ")} />}
        {event.studyId && <Detail label="Study" value={event.studyId} />}
        {event.siteId && <Detail label="Site" value={event.siteId} />}
        {event.subjectId && <Detail label="Subject" value={event.subjectId} />}
      </div>

      {event.attendees && event.attendees.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Attendees
          </h4>
          <div className="rounded-lg border border-border divide-y divide-border">
            {event.attendees.map((a, i) => (
              <div key={i} className="flex justify-between gap-4 px-3 py-2">
                <span className="text-xs text-foreground">{a.name ?? a.email}</span>
                {a.role && <span className="text-xs text-muted-foreground">{a.role}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {event.notes && (
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Notes
          </h4>
          <p className="text-sm text-foreground">{event.notes}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-t border-border pt-4">
        {canComplete && (
          <button
            onClick={handleComplete}
            disabled={updateEvent.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {updateEvent.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Mark Completed
          </button>
        )}
        {canCancel && (
          <button
            onClick={handleCancel}
            disabled={updateEvent.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel Event
          </button>
        )}
        <button
          onClick={handleDelete}
          disabled={deleteEvent.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
        >
          {deleteEvent.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Delete
        </button>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}

